/* ---------------------------------------------------------------------------
   Recommendations for remedial work, and an approximate cost.

   `QC-06`, and the thing the Bridge Inspection Summary has had a box for since
   the guideline was written:

     §5.2  "The Summary Sheet ... will also summarise the recommended repair work
            and give an approximate cost. The Divisional Engineer will use the
            information summarised on this form to assess the maintenance
            priorities for his division."

   THE ARITHMETIC IS ONE LINE:

     amount = quantity × district rate

   Everything else in this file is about what the application is NOT allowed to
   do, because that is where a costing engine goes wrong. It goes wrong by
   filling in a missing quantity, by pricing work somebody has to design first,
   by using last year's rates without saying so, and by presenting a total built
   out of guesses in the same typeface as one built out of measurements.

   FOUR REFUSALS, and each of them produces a line on the estimate rather than a
   silence:

     requires-design   The treatment is Major or Emergency class. DoR's own
                       programme document says the estimate comes after an
                       investigation and a design, so this one refuses. The work
                       still appears on the sheet — it is the most important work
                       on it — carrying "requires design" where the money goes.
     no-quantity       Nothing was measured. §4.3 asks the inspector for an
                       approximate quantity precisely so this does not happen;
                       where it did, the defect is listed and not priced.
     no-rate-table     No approved district rate table has been loaded for this
                       district. The commonest state, and the honest one.
     no-rate           A table exists and does not price this item.

   AND ONE WARNING that is not a refusal: an estimate whose quantities were
   `estimated` rather than `measured` is priced, and says so on every line and in
   its own header. That is the distinction `GR-02` asked for — "the output has to
   say which it used" — carried down to the line.

   TOTALS ARE NOT AVERAGES OF WHAT IS KNOWN. A sheet where three of eight items
   could be priced reports the total of those three AND the count of the five it
   could not, together, always. A total that quietly covered part of the work
   would be read as the cost of the work.
   --------------------------------------------------------------------------- */

import type { Bridge, DefectQuantity, MeasurementMethod, RegisterDefect } from './types.ts';
import {
  isCostable,
  treatmentFor,
  type MaintenanceClass,
  type Treatment,
  type TreatmentWording,
} from './remedial.ts';
import {
  daysSinceApproval,
  findRateTable,
  rateFor,
  RATE_TABLE_STALE_DAYS,
  type RateTable,
} from './rates.ts';
import { methodLabel, trimNumber } from './quantities.ts';

/** Why a recommended item carries no figure. Always shown, never swallowed. */
export type CostRefusal = 'requires-design' | 'no-quantity' | 'no-rate-table' | 'no-rate';

const REFUSAL_TEXT: Record<CostRefusal, string> = {
  'requires-design':
    'Requires detailed investigation, design and cost estimation — refer to the Bridge Unit.',
  'no-quantity': 'No quantity recorded, so no cost can be prepared.',
  'no-rate-table': 'No approved district rate table has been loaded for this district.',
  'no-rate': 'The approved rate table does not price this item.',
};

export function refusalText(r: CostRefusal): string {
  return REFUSAL_TEXT[r];
}

export interface EstimateLine {
  /** The defect this recommendation answers. */
  defectId: string;
  code: string;
  /** Element id where the defect records one, else null. */
  element: string | null;
  /** Where on the structure, as the defect records it. */
  where: string;
  treatment: Treatment;
  klass: MaintenanceClass;
  /** The measured amount, in the treatment's unit — null where there is none. */
  quantity: number | null;
  unit: string;
  /** How the quantity was obtained. Null where there is no quantity. */
  method: MeasurementMethod | null;
  /** Rupees per unit, from the named table. Null where nothing priced it. */
  rate: number | null;
  /** The norm item the rate was analysed from. */
  normRef: string | null;
  /** quantity × rate, or null. */
  amount: number | null;
  /** Why there is no amount. Null when there is one. */
  refusal: CostRefusal | null;
}

/** A named addition applied to the priced subtotal — contingency, VAT. */
export interface EstimateAddition {
  label: string;
  percent: number;
  amount: number;
}

export interface Estimate {
  bridgeId: string;
  lines: readonly EstimateLine[];
  /** The table every rate came from, or null where none was found. */
  table: RateTable | null;
  /** True where the table's approval is older than a year's approval cycle. */
  tableStale: boolean;
  /** Days since the table was approved, where both dates were readable. */
  tableAge: number | null;
  /** Sum of the lines that carry an amount. */
  subtotal: number;
  additions: readonly EstimateAddition[];
  /** subtotal plus additions. */
  total: number;
  /** How many recommendations carry no figure, and why. */
  unpriced: Readonly<Record<CostRefusal, number>>;
  /**
   * The weakest provenance among the quantities that were actually priced.
   *
   * What decides whether the total may be presented without qualification. One
   * estimated quantity in twenty makes the total an estimate, which is why this
   * is the weakest rather than the commonest.
   */
  weakestMethod: MeasurementMethod | null;
}

/**
 * Worst-first, so `weakestMethod` is a comparison rather than a special case.
 *
 * `photo-derived` sits above an eyeball estimate and below a tape reading, which
 * is where it belongs: it is a real measurement of a real reference, and it
 * inherits every error in how that reference was marked, plus the projection
 * error of photographing a surface at an angle. `model-derived` outranks it
 * because register geometry came off an as-built drawing.
 */
const METHOD_RANK: Record<MeasurementMethod, number> = {
  'not-measured': 0,
  estimated: 1,
  'photo-derived': 2,
  'model-derived': 3,
  measured: 4,
};

/** The amount a treatment's quantity key finds on a defect's measurement. */
function amountOf(t: Treatment, q: DefectQuantity | null): number | null {
  if (!q || q.method === 'not-measured' || t.quantityKey === null) return null;
  const v = q[t.quantityKey];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Recommendations and costs for one structure's open defects.
 *
 * `todayIso` is passed in rather than read off a clock so that a check can pin
 * the staleness answer — the same reason `daysSinceApproval` takes one.
 */
export function estimateFor(
  bridge: Bridge,
  defects: readonly RegisterDefect[],
  tables: readonly RateTable[],
  todayIso: string,
  fiscalYear?: string,
  wording?: TreatmentWording,
): Estimate {
  /* A structure with no district recorded can have no district rate table, so
     the estimate refuses to price rather than falling through to somebody
     else's rates. `findRateTable` already returns null for a district it has no
     table for; the empty string is passed so that it takes the same path. */
  const table = findRateTable(tables, bridge.district ?? '', fiscalYear);
  const age = table ? daysSinceApproval(table, todayIso) : null;

  const lines: EstimateLine[] = [];
  const unpriced: Record<CostRefusal, number> = {
    'requires-design': 0,
    'no-quantity': 0,
    'no-rate-table': 0,
    'no-rate': 0,
  };

  for (const d of defects) {
    const treatment = treatmentFor(d.code, d.quantity, wording);
    /* A defect category with no rule produces no recommendation at all rather
       than an empty one. Today every category has one; a tenth code added
       without a rule should be visibly absent from the sheet, not silently
       priced at nothing. */
    if (!treatment) continue;

    const quantity = amountOf(treatment, d.quantity);
    const method = d.quantity && d.quantity.method !== 'not-measured' ? d.quantity.method : null;

    const line: EstimateLine = {
      defectId: d.id,
      code: d.code,
      element: d.element,
      where: d.component ?? d.element ?? 'location not recorded',
      treatment,
      klass: treatment.klass,
      quantity,
      unit: treatment.unit,
      method,
      rate: null,
      normRef: null,
      amount: null,
      refusal: null,
    };

    /* The order of these four matters, and it is the order of how fundamental
       the objection is. Work that needs designing is not priced even when a
       rate exists for something that sounds like it; a defect with no quantity
       is not priced even when the table is loaded and current. Reporting the
       shallowest objection would send somebody to fix a rate table when the
       real answer is that a structural repair needs an engineer. */
    if (!isCostable(treatment.klass) || treatment.quantityKey === null) {
      line.refusal = 'requires-design';
    } else if (quantity === null) {
      line.refusal = 'no-quantity';
    } else if (!table) {
      line.refusal = 'no-rate-table';
    } else {
      const row = rateFor(table, treatment.id);
      if (!row) {
        line.refusal = 'no-rate';
      } else {
        line.rate = row.rate;
        line.normRef = row.normRef;
        line.amount = quantity * row.rate;
      }
    }

    if (line.refusal) unpriced[line.refusal] += 1;
    lines.push(line);
  }

  const priced = lines.filter((l) => l.amount !== null);
  const subtotal = priced.reduce((sum, l) => sum + (l.amount ?? 0), 0);

  const additions: EstimateAddition[] = [];
  if (table && priced.length) {
    if (table.contingencyPct) {
      additions.push({
        label: 'Contingency',
        percent: table.contingencyPct,
        amount: (subtotal * table.contingencyPct) / 100,
      });
    }
    if (table.vatPct) {
      /* On the subtotal plus contingency, which is how a DoR estimate is built
         up. Applied to the bare subtotal it would understate the figure by the
         VAT on the contingency, consistently and by a little — the kind of error
         that survives review because it is nearly right. */
      const base = subtotal + additions.reduce((s, a) => s + a.amount, 0);
      additions.push({
        label: 'VAT',
        percent: table.vatPct,
        amount: (base * table.vatPct) / 100,
      });
    }
  }

  const weakestMethod =
    priced.reduce<MeasurementMethod | null>((worst, l) => {
      if (!l.method) return worst;
      if (!worst) return l.method;
      return METHOD_RANK[l.method] < METHOD_RANK[worst] ? l.method : worst;
    }, null) ?? null;

  return {
    bridgeId: bridge.id,
    lines,
    table,
    tableStale: age !== null && age > RATE_TABLE_STALE_DAYS,
    tableAge: age,
    subtotal,
    additions,
    total: subtotal + additions.reduce((s, a) => s + a.amount, 0),
    unpriced,
    weakestMethod,
  };
}

/**
 * The sentence that has to appear wherever a total does.
 *
 * Not a formatting helper. This is the qualification `GR-02` requires, and
 * putting it in one function is what stops the record, the report and the
 * printed sheet from each deciding for themselves how much to say.
 */
export function totalCaveat(e: Estimate): string {
  const missing = (Object.keys(e.unpriced) as CostRefusal[]).filter((k) => e.unpriced[k] > 0);
  const unpricedCount = missing.reduce((n, k) => n + e.unpriced[k], 0);

  if (!e.table) {
    return 'No approved district rate table has been loaded for this district, so nothing here is priced.';
  }

  const parts: string[] = [
    `Priced against the ${e.table.district} district rates for ${e.table.fiscalYear}, approved ${e.table.approvedOn} by ${e.table.approvedBy}.`,
  ];

  if (e.tableStale) {
    parts.push(
      `That approval is ${e.tableAge} days old and district rates are approved annually — this is very likely not the current table.`,
    );
  }

  if (e.weakestMethod && e.weakestMethod !== 'measured') {
    parts.push(
      `At least one priced quantity was ${methodLabel(e.weakestMethod).toLowerCase()} rather than measured, so the total is an estimate built partly on judgement.`,
    );
  }

  if (unpricedCount > 0) {
    parts.push(
      `${unpricedCount} recommended item${unpricedCount === 1 ? '' : 's'} could not be priced and ${unpricedCount === 1 ? 'is' : 'are'} not in this total.`,
    );
  }

  return parts.join(' ');
}

/**
 * Rupees, grouped the way they are read in Nepal.
 *
 * Lakh-and-crore grouping — 12,34,567 rather than 1,234,567. A DoR estimate is
 * read by people who count in lakhs, and a Western grouping makes a seven-figure
 * total take a second look to place.
 */
export function npr(amount: number): string {
  const rounded = Math.round(amount);
  const s = String(Math.abs(rounded));
  const sign = rounded < 0 ? '-' : '';

  if (s.length <= 3) return `${sign}${s}`;
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  const grouped = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${sign}${grouped},${last3}`;
}

/** One line as a sentence, for the places that cannot draw a table. */
export function describeLine(l: EstimateLine): string {
  const qty = l.quantity === null ? '—' : `${trimNumber(l.quantity)} ${l.unit}`;
  if (l.amount === null) return `${l.treatment.label} · ${qty} · ${refusalText(l.refusal!)}`;
  return `${l.treatment.label} · ${qty} × ${npr(l.rate ?? 0)} = ${npr(l.amount)}`;
}
